import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';
import type { NombreIcono } from './iconos';
import estilos from './MenuAcciones.module.css';

interface MenuAccionesProps {
  children: ReactNode;
  /** Icono del disparador. Por defecto, los tres puntos. */
  icono?: NombreIcono;
  etiqueta?: string;
  /**
   * Alinea el panel con el borde derecho del disparador. Es lo correcto
   * cuando el menú vive al final de una fila: alineado a la izquierda se
   * saldría de la pantalla.
   */
  alineado?: 'izquierda' | 'derecha';
  /**
   * Sólo se comporta como menú en móvil; en pantalla ancha las opciones se
   * muestran sueltas, que es donde hay sitio para ellas.
   */
  soloEnMovil?: boolean;
  className?: string;
}

/**
 * Menú de acciones tras un botón de tres puntos.
 *
 * Una fila con cuatro botones cabe en una tabla de escritorio; en una tarjeta
 * de móvil ocupa toda la anchura y deja los datos en un rincón. Recogidos aquí,
 * la tarjeta vuelve a ser lo que interesa y las acciones siguen a un toque.
 *
 * Se cierra al elegir una opción, al tocar fuera y con Escape: las tres
 * maneras en que uno espera cerrar algo así.
 */
export function MenuAcciones({
  children,
  icono = 'masOpciones',
  etiqueta = 'Más acciones',
  alineado = 'derecha',
  soloEnMovil = false,
  className,
}: MenuAccionesProps) {
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!abierto) return;

    const alPulsarFuera = (evento: MouseEvent) => {
      if (!contenedor.current?.contains(evento.target as Node)) setAbierto(false);
    };

    const alPulsarTecla = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAbierto(false);
    };

    document.addEventListener('mousedown', alPulsarFuera);
    document.addEventListener('keydown', alPulsarTecla);

    return () => {
      document.removeEventListener('mousedown', alPulsarFuera);
      document.removeEventListener('keydown', alPulsarTecla);
    };
  }, [abierto]);

  const clases = [
    estilos.contenedor,
    soloEnMovil ? estilos.soloEnMovil : '',
    alineado === 'izquierda' ? estilos.aIzquierda : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={contenedor} className={clases}>
      <button
        type="button"
        className={estilos.disparador}
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-haspopup="menu"
        aria-label={etiqueta}
        title={etiqueta}
      >
        <Icon nombre={icono} tamano={18} />
      </button>

      {/*
        Se cierra al elegir cualquier opción: capturar el clic aquí evita tener
        que acordarse de cerrarlo en cada acción que se le pase.
      */}
      <div
        className={[estilos.panel, abierto ? estilos.panelAbierto : ''].filter(Boolean).join(' ')}
        role="menu"
        onClick={() => setAbierto(false)}
      >
        {children}
      </div>
    </div>
  );
}
